import React, { forwardRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type TextFieldProps } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker as DsDatePicker } from '@natter-co/design-system';
import { ClearButton } from 'common/components/Buttons/ClearButton';
import { DEFAULT_DATE_FORMAT } from 'common/constants';
import { VISUAL_REFRESH } from 'common/utils';
import { isToday } from 'date-fns';
import { CalendarIcon } from 'icons';

import { TextField } from '../TextField';
import { toDsProps } from './toDsProps';

export type DatePickerProps = {
  disabled?: boolean;
  error?: boolean;
  format?: string;
  fullWidth?: boolean;
  /**
   * Drop the label once the field holds a value, which is what MUI's outlined field needs to stop
   * the notched label repeating the date below it.
   *
   * Honoured by the MUI branch ONLY. The refreshed field is a floating-label field: filled is the
   * state its label was drawn for, so it keeps showing it, shrunk, above the value.
   *
   * TODO(HNS-264): delete with the wrapper. This exists so a caller can ask for the legacy look
   * explicitly instead of signalling it by withholding `label` - which is what it used to do, and
   * which cost the refreshed field both its visible name and a stable accessible one. See the note
   * in `toDsProps`.
   */
  hideLabelWhenFilled?: boolean;
  hint?: string;
  label?: string;
  name?: string;
  placeholder?: string;
  required?: boolean;
  value: Date | null;
  minDate?: Date | undefined;
  variant?: 'filled' | 'outlined' | 'standard';
  onAccept?(date: Date | null): void;
  onBlur?(): void;
  onChange(value: Date | null): void;
  onClose?(): void;
  onOpen?(): void;
  /**
   * Called when the refreshed field starts refusing a typed entry and when it stops, with the value
   * it accepted. While refused, that value is all `onChange` has heard, so a form needs this to stop
   * a submit carrying it; handing back an invalid `Date` as `value` meanwhile is safe. Never called
   * by the MUI branch, which reports an unparsed entry through `onChange` as an invalid `Date`.
   */
  onRefusedEntryChange?(refused: boolean, accepted: Date | null): void;
};

export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      error,
      format = DEFAULT_DATE_FORMAT,
      fullWidth = true,
      hideLabelWhenFilled,
      hint,
      label,
      placeholder = 'Select a date',
      required,
      value,
      minDate,
      variant,
      disabled,
      onAccept,
      onBlur,
      onChange,
      onClose,
      onOpen,
      onRefusedEntryChange,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation('common', { keyPrefix: 'datePicker' });
    const [isOpen, setIsOpen] = useState(false);

    const handleAccept = useCallback(
      (date: Date | null) => {
        onChange(date);
        onAccept?.(date);
      },
      [onAccept, onChange]
    );

    const handleOpen = useCallback(() => {
      if (disabled) return;

      setIsOpen(true);
      onOpen?.();
    }, [onOpen, disabled]);

    const handleClose = useCallback(() => {
      setIsOpen(false);
      onClose?.();
    }, [onClose]);

    const handleClearInput = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onChange(null);
      },
      [onChange]
    );

    /**
     * The visual-refresh gate lives HERE, not at the call sites: they keep the MUI-shaped API and
     * never mention the flag. `toDsProps` returns `null` for anything DS 2.0 cannot express, which
     * falls through to the MUI-X implementation below.
     */
    const dsProps = VISUAL_REFRESH
      ? toDsProps({ error, format, fullWidth, hint, label, minDate, placeholder, variant })
      : null;

    if (dsProps) {
      return (
        <DsDatePicker
          {...dsProps}
          disabled={disabled}
          messages={{
            format: (_hint, example) => t('format', { example }),
            notReal: t('notReal'),
            // `min` is today unless the caller asked for a later day, and "today" reads better than
            // today's date spelled out.
            beforeMin: (limit) =>
              dsProps.min !== undefined && isToday(dsProps.min) ? t('beforeToday') : t('beforeMin', { limit }),
            afterMax: (limit) => t('afterMax', { limit }),
          }}
          // The legacy wrapper types its ref as `HTMLInputElement` and hands it to MUI-X as
          // `inputRef`, so it has always pointed at the input. The refreshed field forwards to its
          // own control, which is that same input - which is what keeps react-hook-form's
          // focus-on-append working when a host adds an occurrence.
          ref={ref}
          required={required}
          value={value}
          onBlur={onBlur}
          onEntryErrorChange={(entryError, accepted) => onRefusedEntryChange?.(entryError !== null, accepted)}
          // The same handlers the MUI-X branch wires, so a caller's `onOpen`/`onClose` survive the
          // swap, and `handleOpen`'s disabled guard with them.
          onOpenChange={(isOpen) => (isOpen ? handleOpen() : handleClose())}
          onValueChange={(date) => {
            onChange(date);
            onAccept?.(date);
          }}
          {...props}
        />
      );
    }

    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <MuiDatePicker
          disablePast
          minDate={minDate}
          format={format}
          inputRef={ref}
          // The legacy look: the value replaces the label rather than sitting under it. Applied
          // here rather than at the call site so BOTH branches can be driven by one `label` prop -
          // the refreshed field needs the real name in every state, and inferring it from a
          // missing prop is what broke it.
          label={hideLabelWhenFilled && value ? undefined : label}
          open={isOpen}
          slots={{
            openPickerIcon: CalendarIcon,
            textField: TextField,
          }}
          slotProps={{
            openPickerButton: {
              disableRipple: true,
              sx: ({ palette }) => ({
                color: palette.grey[600],

                ':hover': {
                  backgroundColor: 'transparent',
                },
              }),
            },
            textField: {
              error,
              fullWidth,
              hint,
              placeholder,
              required,
              suffix: value && !disabled ? <ClearButton onClick={handleClearInput} /> : undefined,
              onBlur,
              onClick: handleOpen,
              variant,
            } as unknown as TextFieldProps,
            field: {
              readOnly: true,
              sx: {
                '& .MuiInputBase-root, input': {
                  cursor: disabled ? 'default' : 'pointer',
                },
                '& .MuiInputBase-root::before, .MuiInputBase-root.MuiInput-root:hover:before, .MuiInputBase-root::after':
                  {
                    borderBottom: 'none',
                  },
              },
            },
          }}
          value={value}
          onAccept={handleAccept}
          onChange={onChange}
          onClose={handleClose}
          onOpen={handleOpen}
          disabled={disabled}
          {...props}
        />
      </LocalizationProvider>
    );
  }
);
